import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import Loading from '../components/Loading';
import { listSuppliers } from '../services/suppliers';
import { listInvoices } from '../services/invoices';
import { formatCurrency, formatDateShort } from '../utils/formatters';

/**
 * PO Tracker — fourth tab under /invoices. POs are grouped by type into
 * four sections (per Jillian, Aug 2026):
 *
 *   • Yearly POs / Quarterly POs — blanket-style: carry a dollar balance
 *     drawn down by Paid invoices for the PO's plant(s) issued inside the
 *     PO's window. Only PAID invoices deduct; issued-but-unpaid ones show
 *     in the Pending column as a heads-up. Rows expand to a per-invoice
 *     running balance. (The old "Blanket PO" type is now Yearly.)
 *   • Monthly POs — a flat per-month PO; no balance (each month's invoice
 *     equals the PO amount). Listed for reference only.
 *   • PO Pending — plants awaiting a PO number. Temporary; re-filed into
 *     one of the real types once the number arrives.
 *
 * Balance flag thresholds (Yearly/Quarterly):
 *   • Remaining ≤ $0           → red "Request New PO"
 *   • Remaining < 20% of total → amber "Running Low"
 *   • Otherwise                → green
 *
 * Data model — POs live per plant on each supplier's plant assignments (a
 * `po` object with type, number, amount, and — for Yearly/Quarterly — the
 * date window), set on the Supplier form's Plant POs section.
 */
export default function PoTracker() {
  const { isDirector } = useAuth();

  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [supRows, invRows] = await Promise.all([
        listSuppliers(),
        listInvoices(),
      ]);
      setSuppliers(supRows || []);
      setInvoices(invRows || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpanded = (code) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  // ---- Categorize every supplier PO by type --------------------------
  const poData = useMemo(() => {
    const invoicesBySupplier = new Map();
    for (const inv of invoices || []) {
      const sc = String(inv.SupplierCode || '').trim().toUpperCase();
      if (!sc) continue;
      if (!invoicesBySupplier.has(sc)) invoicesBySupplier.set(sc, []);
      invoicesBySupplier.get(sc).push(inv);
    }

    const yearly = [];
    const quarterly = [];
    const monthly = [];
    const pending = [];

    for (const s of suppliers || []) {
      if (s.PoRequired !== 'Yes') continue;
      const sc = String(s.SupplierCode || '').trim().toUpperCase();
      const assignments = s.plantAssignments || [];
      const totalAssignments = assignments.length;

      const pos = assignments
        .filter((pa) => pa?.po?.type)
        .map((pa) => ({
          plantCode: String(pa.plantCode || '').trim().toUpperCase(),
          po: pa.po,
          assignment: pa,
        }));

      // Pool POs that share a non-empty number + amount + window (the
      // form's "All Plants" option), so one PO doesn't get counted once
      // per plant. Unnumbered POs stay separate per plant.
      const groupPos = (list) => {
        const groups = new Map();
        for (const bp of list) {
          const num = String(bp.po.number || '').trim().toUpperCase();
          const key = num
            ? [
                bp.po.type,
                num,
                Number(bp.po.amount) || 0,
                toIsoDate(bp.po.initiationDate),
                toIsoDate(bp.po.expirationDate),
              ].join('|')
            : `__solo__:${bp.plantCode}`;
          if (!groups.has(key)) {
            groups.set(key, {
              po: bp.po,
              assignment: bp.assignment,
              plantCodes: [],
            });
          }
          groups.get(key).plantCodes.push(bp.plantCode);
        }
        return Array.from(groups.values());
      };
      const plantLabelFor = (plantCodes) =>
        plantCodes.length > 1 && plantCodes.length === totalAssignments
          ? 'All Plants'
          : plantCodes.join(', ');

      // Yearly + Quarterly — balance rows.
      const balanceGroups = groupPos(
        pos.filter(
          (bp) => bp.po.type === 'Yearly PO' || bp.po.type === 'Quarterly PO',
        ),
      );
      // Lone-balance flag lets legacy no-plant invoices still draw down a
      // supplier's single balance PO.
      const singleBalance = balanceGroups.length === 1;
      for (const g of balanceGroups) {
        const row = buildBalanceRow({
          supplier: s,
          supplierCode: sc,
          po: g.po,
          assignment: g.assignment,
          plantCodes: g.plantCodes,
          plantLabel: plantLabelFor(g.plantCodes),
          single: singleBalance,
          invoicesBySupplier,
        });
        if (g.po.type === 'Quarterly PO') quarterly.push(row);
        else yearly.push(row);
      }

      // Monthly — list only, no balance.
      for (const g of groupPos(pos.filter((bp) => bp.po.type === 'Monthly PO'))) {
        monthly.push({
          supplier: s,
          supplierCode: sc,
          plantLabel: plantLabelFor(g.plantCodes),
          poNumber: String(g.po.number || '').trim(),
          amount: Number(g.po.amount) || 0,
          rowKey: `${sc}:M:${g.plantCodes.join('+')}`,
        });
      }

      // PO Pending — list of plants awaiting a number.
      for (const g of groupPos(pos.filter((bp) => bp.po.type === 'PO Pending'))) {
        pending.push({
          supplier: s,
          supplierCode: sc,
          plantLabel: plantLabelFor(g.plantCodes),
          amount: Number(g.po.amount) || 0,
          rowKey: `${sc}:P:${g.plantCodes.join('+')}`,
        });
      }
    }

    const byName = (a, b) => {
      const an = (a.supplier.Title || a.supplierCode || '').toLowerCase();
      const bn = (b.supplier.Title || b.supplierCode || '').toLowerCase();
      if (an !== bn) return an.localeCompare(bn);
      return (a.plantLabel || '').localeCompare(b.plantLabel || '');
    };
    const bySeverityThenName = (a, b) => {
      if (a.severity !== b.severity) return b.severity - a.severity;
      return byName(a, b);
    };
    yearly.sort(bySeverityThenName);
    quarterly.sort(bySeverityThenName);
    monthly.sort(byName);
    pending.sort(byName);
    return { yearly, quarterly, monthly, pending };
  }, [suppliers, invoices]);

  // ---- Render guards --------------------------------------------------
  if (!isDirector) {
    return (
      <div className="card">
        <div className="card-title">PO Tracker</div>
        <p className="muted">Only Directors can view the PO Tracker.</p>
      </div>
    );
  }
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load PO Tracker</div>
        <p>{error.message || 'Unknown error.'}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1>PO Tracker</h1>
        <div className="actions">
          <button className="btn btn-outline" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      <p className="muted" style={{ marginTop: 0 }}>
        POs grouped by type. Yearly and Quarterly POs carry a balance drawn
        down by Paid invoices. Pending shows invoices issued but not yet
        paid. Monthly POs don't carry a balance. POs Pending lists plants
        still awaiting a PO number. Set up a supplier's plant POs on the{' '}
        <Link to="/suppliers">Suppliers</Link> page.
      </p>

      <PoBalanceSection
        title="Yearly POs"
        rows={poData.yearly}
        emptyText="No Yearly POs"
        expanded={expanded}
        toggleExpanded={toggleExpanded}
      />
      <PoBalanceSection
        title="Quarterly POs"
        rows={poData.quarterly}
        emptyText="No Quarterly POs"
        expanded={expanded}
        toggleExpanded={toggleExpanded}
      />
      <PoMonthlySection rows={poData.monthly} />
      <PoPendingSection rows={poData.pending} />
    </>
  );
}

// ---- Section: heading wrapper ---------------------------------------------

function SectionHead({ title, count }) {
  return (
    <h2 style={{ fontSize: 16, margin: '26px 0 10px' }}>
      {title}
      {count > 0 && (
        <span
          className="muted"
          style={{ fontSize: 13, fontWeight: 400, marginLeft: 8 }}
        >
          {count} PO{count === 1 ? '' : 's'}
        </span>
      )}
    </h2>
  );
}

// ---- Section: Yearly / Quarterly (balance table) --------------------------

function PoBalanceSection({ title, rows, emptyText, expanded, toggleExpanded }) {
  return (
    <div>
      <SectionHead title={title} count={rows.length} />
      {rows.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            {emptyText}
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="mini-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>Supplier</th>
                <th>Plant</th>
                <th>PO Number</th>
                <th>Start Date</th>
                <th>Expires</th>
                <th style={{ textAlign: 'right' }}>PO Amount</th>
                <th style={{ textAlign: 'right' }}>Paid to Date</th>
                <th style={{ textAlign: 'right' }}>Pending</th>
                <th style={{ textAlign: 'right' }}>Remaining</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Fragment key={row.rowKey}>
                  <tr
                    onClick={() => toggleExpanded(row.rowKey)}
                    style={{ cursor: 'pointer' }}
                    title="Click to show the invoices behind this balance"
                  >
                    <td
                      style={{
                        textAlign: 'center',
                        color: 'var(--brand-muted)',
                        fontSize: 11,
                      }}
                    >
                      {expanded.has(row.rowKey) ? '▼' : '▶'}
                    </td>
                    <td>
                      <Link
                        to={`/suppliers/${encodeURIComponent(row.supplierCode)}`}
                        style={{ color: 'var(--brand-ink)' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="muted" style={{ marginRight: 6 }}>
                          {row.supplierCode}
                        </span>
                        {row.supplier.Title}
                      </Link>
                    </td>
                    <td>{row.plantLabel || '—'}</td>
                    <td>
                      {row.poNumbers.length > 0 ? (
                        row.poNumbers.join(', ')
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {row.initiationIso
                        ? formatDateShort(row.initiationIso)
                        : '—'}
                    </td>
                    <td>
                      {row.expirationIso ? (
                        formatDateShort(row.expirationIso)
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(row.amount)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.paidTotal ? formatCurrency(row.paidTotal) : '—'}
                    </td>
                    <td
                      style={{ textAlign: 'right', color: 'var(--brand-muted)' }}
                      title="Issued but not yet paid — will deduct once marked Paid"
                    >
                      {row.pendingTotal
                        ? formatCurrency(row.pendingTotal)
                        : '—'}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontWeight: 700,
                        color:
                          row.severity === 2
                            ? 'var(--brand-danger)'
                            : row.severity === 1
                              ? '#ca8a04'
                              : 'var(--brand-success, #16a34a)',
                      }}
                    >
                      {formatCurrency(row.remaining)}
                      {row.severity === 2 && (
                        <span
                          style={{
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          Request New PO
                        </span>
                      )}
                      {row.severity === 1 && (
                        <span
                          style={{
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          Running Low
                        </span>
                      )}
                    </td>
                  </tr>
                  {expanded.has(row.rowKey) && (
                    <tr>
                      <td colSpan={10} style={{ padding: 0 }}>
                        <PoInvoiceBreakdown row={row} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Section: Monthly (list, no balance) ----------------------------------

function PoMonthlySection({ rows }) {
  return (
    <div>
      <SectionHead title="Monthly POs" count={rows.length} />
      {rows.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No Monthly POs
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <p
            className="muted"
            style={{ margin: 0, padding: '10px 14px', fontSize: 12 }}
          >
            No balance tracked — each month's invoice equals the PO amount.
          </p>
          <table className="mini-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Plant</th>
                <th>PO Number</th>
                <th style={{ textAlign: 'right' }}>Monthly Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.rowKey}>
                  <td>
                    <Link
                      to={`/suppliers/${encodeURIComponent(row.supplierCode)}`}
                      style={{ color: 'var(--brand-ink)' }}
                    >
                      <span className="muted" style={{ marginRight: 6 }}>
                        {row.supplierCode}
                      </span>
                      {row.supplier.Title}
                    </Link>
                  </td>
                  <td>{row.plantLabel || '—'}</td>
                  <td>
                    {row.poNumber ? (
                      row.poNumber
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {row.amount ? formatCurrency(row.amount) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Section: PO Pending (awaiting a number) ------------------------------

function PoPendingSection({ rows }) {
  return (
    <div>
      <SectionHead title="POs Pending" count={rows.length} />
      {rows.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No pending POs
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <p
            className="muted"
            style={{ margin: 0, padding: '10px 14px', fontSize: 12 }}
          >
            Waiting on a PO number — re-file into Yearly / Quarterly / Monthly
            once it arrives.
          </p>
          <table className="mini-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Plant</th>
                <th style={{ textAlign: 'right' }}>Expected Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.rowKey}>
                  <td>
                    <Link
                      to={`/suppliers/${encodeURIComponent(row.supplierCode)}`}
                      style={{ color: 'var(--brand-ink)' }}
                    >
                      <span className="muted" style={{ marginRight: 6 }}>
                        {row.supplierCode}
                      </span>
                      {row.supplier.Title}
                    </Link>
                  </td>
                  <td>{row.plantLabel || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {row.amount ? (
                      formatCurrency(row.amount)
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        background: '#fef3c7',
                        color: '#92400e',
                        borderRadius: 20,
                        padding: '2px 10px',
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                      }}
                    >
                      Awaiting PO #
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Expanded invoice breakdown -------------------------------------------

function PoInvoiceBreakdown({ row }) {
  if (row.detailRows.length === 0) {
    return (
      <p className="muted" style={{ margin: 0, padding: '10px 14px 14px 44px' }}>
        No invoices issued since this PO's start date.
      </p>
    );
  }
  return (
    <div style={{ padding: '4px 14px 14px 44px' }}>
      <table className="mini-table" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>Invoice #</th>
            <th>Issued</th>
            <th>Status</th>
            <th style={{ textAlign: 'right' }}>Total</th>
            <th style={{ textAlign: 'right' }}>Deducted</th>
            <th style={{ textAlign: 'right' }}>Balance After</th>
          </tr>
        </thead>
        <tbody>
          {row.detailRows.map(({ invoice, total, counted, cancelled, balanceAfter }) => (
            <tr
              key={invoice.id}
              style={
                cancelled
                  ? { color: 'var(--brand-muted)', textDecoration: 'line-through' }
                  : counted
                    ? undefined
                    : { color: 'var(--brand-muted)' }
              }
            >
              <td>
                <Link to={`/invoices/view/${encodeURIComponent(invoice.id)}`}>
                  {invoice.InvoiceNumber || invoice.id}
                </Link>
              </td>
              <td>
                {invoice.IssueDate ? formatDateShort(invoice.IssueDate) : '—'}
              </td>
              <td>{invoice.Status || '—'}</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(total)}</td>
              <td style={{ textAlign: 'right' }}>
                {counted ? (
                  formatCurrency(total)
                ) : cancelled ? (
                  '—'
                ) : (
                  <em style={{ fontSize: 12 }}>pending</em>
                )}
              </td>
              <td style={{ textAlign: 'right', fontWeight: counted ? 600 : 400 }}>
                {counted ? formatCurrency(balanceAfter) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Helpers ---------------------------------------------------------------

// Build one balance-carrying PO row (Yearly / Quarterly). Only PAID
// invoices for the PO's plant(s), issued inside the window, deduct.
function buildBalanceRow({
  supplier,
  supplierCode,
  po,
  assignment,
  plantCodes,
  plantLabel,
  single,
  invoicesBySupplier,
}) {
  const sc = supplierCode;
  const plantSet = new Set(plantCodes);
  const amount = Number(po.amount) || 0;
  const initiationIso = toIsoDate(po.initiationDate);
  const expirationIso = toIsoDate(po.expirationDate);

  const relevant = (invoicesBySupplier.get(sc) || [])
    .filter((inv) => (inv.Status || '') !== 'Draft')
    .filter((inv) => {
      const codes = (inv.plantCodes || []).map((c) => String(c).toUpperCase());
      if (codes.length === 0) return single;
      return codes.some((c) => plantSet.has(c));
    })
    .filter((inv) => {
      const d = toIsoDate(inv.IssueDate);
      if (initiationIso && (!d || d < initiationIso)) return false;
      if (expirationIso && (!d || d > expirationIso)) return false;
      return true;
    })
    .sort((a, b) =>
      toIsoDate(a.IssueDate).localeCompare(toIsoDate(b.IssueDate)),
    );

  let running = amount;
  const detailRows = relevant.map((inv) => {
    const total = Number(inv.Total) || 0;
    const isPaid = (inv.Status || '') === 'Paid';
    const isCancelled = (inv.Status || '') === 'Cancelled';
    if (isPaid) running -= total;
    return {
      invoice: inv,
      total,
      counted: isPaid,
      cancelled: isCancelled,
      balanceAfter: isPaid ? running : null,
    };
  });
  const paidTotal = detailRows
    .filter((r) => r.counted)
    .reduce((acc, r) => acc + r.total, 0);
  const pendingTotal = detailRows
    .filter((r) => !r.counted && !r.cancelled)
    .reduce((acc, r) => acc + r.total, 0);
  const remaining = amount - paidTotal;

  const explicitPoNumber = String(po.number || '').trim();
  const fallbackPoNumber = String(assignment?.currentPO || '').trim();
  const poNumbers = explicitPoNumber
    ? [explicitPoNumber]
    : fallbackPoNumber
      ? [fallbackPoNumber]
      : [];

  return {
    supplier,
    supplierCode: sc,
    plantLabel,
    rowKey: `${sc}:${po.type}:${plantCodes.join('+')}`,
    poNumbers,
    amount,
    initiationIso,
    expirationIso,
    paidTotal,
    pendingTotal,
    remaining,
    detailRows,
    severity:
      remaining <= 0 ? 2 : amount > 0 && remaining < amount * 0.2 ? 1 : 0,
  };
}

// Normalize a SharePoint date (may carry a time component) to YYYY-MM-DD
// for string comparison; empty string when absent.
function toIsoDate(value) {
  const m = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
